import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  useTheme,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const SystemAnalytics = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    businessGrowth: [],
    topBusinesses: [],
    userActivity: [],
    systemMetrics: {
      totalRevenue: 0,
      activeUsers: 0,
      newBusinessesThisMonth: 0,
      systemUptime: '99.9%'
    }
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const statsResponse = await api.get('/super-admin/stats');
      const data = statsResponse.data;

      // Map API response to component state
      setAnalytics({
        businessGrowth: data.businessGrowth || [],
        topBusinesses: (data.topBusinesses || []).map(business => ({
          name: business.name,
          users: parseInt(business.user_count) || 0,
          branches: parseInt(business.branch_count) || 0,
          status: business.is_active ? 'active' : 'inactive',
          revenue: parseFloat(business.total_revenue) || 0
        })),
        systemMetrics: {
          totalRevenue: parseFloat(data.stats?.total_revenue) || 0,
          activeUsers: parseInt(data.stats?.total_users) || 0,
          newBusinessesThisMonth: parseInt(data.stats?.new_businesses_this_month) || 0,
          systemUptime: '99.9%'
        }
      });

      setError(null);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const MetricCard = ({ title, value, change, icon, color = 'primary' }) => (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        border: 1,
        borderColor: theme.palette.divider,
        borderRadius: 2
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {React.cloneElement(icon, { 
            sx: { 
              color: theme.palette[color].main, 
              mr: 1,
              fontSize: 24
            } 
          })}
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        <Typography 
          variant="h4" 
          component="div" 
          sx={{ 
            mb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {change && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {change > 0 ? (
              <TrendingUpIcon sx={{ color: 'success.main', mr: 0.5, fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ color: 'error.main', mr: 0.5, fontSize: 18 }} />
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                color: change > 0 ? 'success.main' : 'error.main',
                fontWeight: 500
              }}
            >
              {Math.abs(change)}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        System Analytics
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Total Revenue"
            value={`KES ${analytics.systemMetrics.totalRevenue.toLocaleString()}`}
            icon={<TrendingUpIcon />}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Active Users"
            value={analytics.systemMetrics.activeUsers}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="New Businesses"
            value={analytics.systemMetrics.newBusinessesThisMonth}
            icon={<BusinessIcon />}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="System Uptime"
            value={analytics.systemMetrics.systemUptime}
            icon={<StoreIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Business Growth Trend */}
      <Card 
        elevation={0}
        sx={{ 
          mb: 4,
          backgroundColor: theme.palette.background.paper,
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Growth Trends (Last 6 Months)
          </Typography>
          {analytics.businessGrowth.length > 0 ? (
            <Grid container spacing={2}>
              {analytics.businessGrowth.map((data, index) => (
                <Grid size={2} key={data.month}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {data.month}
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {data.businesses}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Businesses
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.secondary.main }}>
                        {data.users}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Users
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((data.businesses / 50) * 100, 100)}
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              No growth data available yet
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Top Performing Businesses */}
      <Card 
        elevation={0}
        sx={{ 
          backgroundColor: theme.palette.background.paper,
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Top Performing Businesses
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Business Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Users</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Branches</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Revenue</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.topBusinesses.length > 0 ? (
                  analytics.topBusinesses.map((business, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <BusinessIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {business.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {business.users}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {business.branches}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          KES {business.revenue.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={business.status}
                          color={business.status === 'active' ? 'success' : 'error'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        No business data available yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemAnalytics;